// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/applyStreamSnapshot.test.ts
import {
  type AskPunttAgentStep,
  type AskPunttCheckProgress,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";

import { type StreamingState } from "../../hooks/useAskPunttStream";

/**
 * The message id warhol publishes a turn's snapshot under.
 *
 * The `w` is warhol's own spelling (`runtime/clients/ask_puntt_stream.py::
 * STREAM_MESSAGE_PREFIX`) and it matters here: the socket subscription key is
 * `chatId:messageId`, so an id carrying a colon would split into the wrong
 * pair (`hooks/useAskPunttStream.ts`, `key.split(":")`).
 */
export const STREAM_MESSAGE_PREFIX = "w";

/** What a message id would have to look like to be a stream snapshot's. */
const STREAM_MESSAGE_ID = /^w[^:]+$/;

/**
 * The stream key of the turn waiting on an answer, or null when none is.
 *
 * Two shapes of history mean "not answered yet", and BOTH have to arm the
 * subscription:
 *
 * - Warhol answers whole, so a turn nobody has mirrored yet leaves the
 *   person's own message last.
 * - Christo mirrors a RUNNING warhol turn onto a pending assistant row
 *   (`upsertWarholTurnPlaceholder`, which requires the transcript to end with
 *   the person's message and then appends to it). Mid-review the tail is
 *   therefore an assistant row even though the answer has not landed.
 *
 * Reading only the first shape tore the subscription — and with it the socket
 * and its `/stream` poll fallback — down a couple of seconds into every review,
 * which is the whole window the rail's per-check rows have to arrive in. The
 * turn's key stays the asker's own id: warhol publishes under
 * `w<userMessageId>`, never under the placeholder's `_id`.
 *
 * A tail the answer has replaced (`status` anything but `pending`) is over, and
 * that is when the subscription is torn down.
 */
export function streamMessageIdFor(
  messages: readonly AskPunttMessage[] | undefined,
): string | null {
  let tail: AskPunttMessage | undefined;
  for (let index = (messages?.length ?? 0) - 1; index >= 0; index -= 1) {
    const message = messages?.[index];
    if (message && !message.hidden) {
      tail = message;
      break;
    }
  }
  if (!tail) return null;
  if (tail.role === "user") return `${STREAM_MESSAGE_PREFIX}${tail._id}`;
  if (tail.status !== "pending") return null;

  // The placeholder christo mirrors a running turn onto. Subscribe under the
  // message warhol was handed, which is the last visible user row.
  for (let index = (messages?.length ?? 0) - 1; index >= 0; index -= 1) {
    const message = messages?.[index];
    if (message && !message.hidden && message.role === "user") {
      return `${STREAM_MESSAGE_PREFIX}${message._id}`;
    }
  }
  return null;
}

/**
 * Whether a live snapshot has anything the rail or the chips can draw.
 *
 * A snapshot is published for the whole turn once any check settles, and its
 * `status`/`content` fields say nothing a v2 chat acts on — a frame carrying
 * only those must leave the thread byte-identical, or an empty row appears the
 * moment the first publish lands.
 */
function liveFields(snapshot: StreamingState): Partial<AskPunttMessage> | null {
  const agentSteps = (snapshot.agentSteps ?? []).filter(
    (step): step is AskPunttAgentStep =>
      !!step && typeof step === "object" && !Array.isArray(step),
  );
  const checkProgress = snapshot.checkProgress ?? [];
  if (agentSteps.length === 0 && checkProgress.length === 0) return null;
  return {
    ...(agentSteps.length > 0 ? { agentSteps } : {}),
    ...(checkProgress.length > 0
      ? { checkProgress: checkProgress as AskPunttCheckProgress[] }
      : {}),
  };
}

/**
 * The live snapshot, laid onto the row the running turn belongs to.
 *
 * This is the whole of the v2 stream integration's state: the history poll
 * stays the source of truth for the ANSWER, and the socket only adds what has
 * happened so far — warhol's own `checkProgress` and the `agentSteps` whose
 * `subagentOutcomes` the fan-out rows read. Nothing here writes to the server.
 *
 * The row is the pending turn's: the tail assistant row if christo has already
 * placed one, and a synthesised one after the person's message otherwise —
 * warhol writes no placeholder while it reviews, so the synthesised case is the
 * normal one and not a fallback. Returns the SAME array when there is nothing
 * live to show, so a `useMemo` over it does not churn the thread on every idle
 * frame.
 */
export function applyStreamSnapshot(
  messages: AskPunttMessage[],
  snapshot: StreamingState | null | undefined,
): AskPunttMessage[] {
  if (!snapshot) return messages;
  const live = liveFields(snapshot);
  if (!live) return messages;

  let tailIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (!messages[index]?.hidden) {
      tailIndex = index;
      break;
    }
  }
  if (tailIndex < 0) return messages;
  const tail = messages[tailIndex];

  if (tail.role === "assistant") {
    return messages.map((message, index) =>
      index === tailIndex ? { ...message, ...live } : message,
    );
  }

  const messageId = streamMessageIdFor(messages);
  // why-not: no `_id` fallback invented here. Without a stream id there is no
  // snapshot either, so this row would only ever be keyed by something no
  // reader can match; `streamMessageIdFor` and this share one definition.
  if (!messageId || !STREAM_MESSAGE_ID.test(messageId)) return messages;

  return [
    ...messages,
    {
      _id: messageId,
      chatId: tail.chatId,
      role: "assistant" as const,
      content: "",
      status: "pending" as const,
      createdAt: tail.createdAt,
      ...live,
    },
  ];
}
